/**
 * "No credit for this billed action" errors (analysis, re-analysis, chat
 * re-assessment). The backend answers every one with HTTP 402 and a coded
 * detail `{ code, message }` (`credit_service.no_credit_http_exception`):
 * `no_credits` when the credit balance is spent (buy credits),
 * `subscription_required` when a B2C user has no entitled plan (choose a
 * plan), `followup_credit_required` for a chat target-level re-assessment. 429 is only for rate-style limits (chat quota, trial chat
 * messages). Chat and ICF progress share this helper so both show the same
 * copy. The card-backed trial's `trial_analysis_used` block has its own
 * helper (`trialAnalysisUsed.ts`).
 */
export const NO_CREDITS_CODE = 'no_credits';
export const FOLLOWUP_CREDIT_REQUIRED_CODE = 'followup_credit_required';
export const SUBSCRIPTION_REQUIRED_CODE = 'subscription_required';

export const NO_CREDITS_MESSAGE_KEY = 'dashboard.noCreditsToAnalyze';
export const SUBSCRIPTION_REQUIRED_MESSAGE_KEY = 'dashboard.planRequiredToAnalyze';

const NO_CREDIT_KEYS: Record<string, string> = {
  [NO_CREDITS_CODE]: NO_CREDITS_MESSAGE_KEY,
  [SUBSCRIPTION_REQUIRED_CODE]: SUBSCRIPTION_REQUIRED_MESSAGE_KEY,
  [FOLLOWUP_CREDIT_REQUIRED_CODE]: 'chatLibrary.errors.followupCreditRequired',
};

function detailCodeOf(error: unknown): string | null {
  const e = error as { detail?: unknown; response?: { data?: { detail?: unknown } } } | null | undefined;
  for (const candidate of [e?.detail, e?.response?.data?.detail]) {
    const code = (candidate as { code?: unknown } | null | undefined)?.code;
    if (candidate && typeof candidate === 'object' && typeof code === 'string') return code;
  }
  return null;
}

/** Translation key for a no-credit block, or null for any other error. */
export function noCreditErrorKey(error: unknown): string | null {
  const code = detailCodeOf(error);
  return code ? NO_CREDIT_KEYS[code] ?? null : null;
}

export function isNoCreditError(error: unknown): boolean {
  return noCreditErrorKey(error) !== null;
}

type Translate = (key: string, ...rest: any[]) => string;

/** Localized no-credit copy for `error`, or null (callers keep their message). */
export function noCreditMessage(t: Translate, error: unknown): string | null {
  const key = noCreditErrorKey(error);
  return key ? t(key) : null;
}
